import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { ArrowUp, ArrowDown, ArrowUpDown, Star } from "lucide-react";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, Input, SearchBar, Pagination } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useCustomers, useToggleCustomerMarked } from "../hooks";
import { useCategories } from "../../categories/hooks";
import { cleanObject } from "../../../helpers/clean-object";
import {
  CustomerStatus,
  FindBySource,
  CustomerSortField,
  SortOrder,
} from "../../../__generated__/globalTypes";
import { customerStatusLabel, findBySourceLabel } from "../../../helpers/enum-labels";

const PAGE_SIZE = 25;

interface ISearchForm {
  search?: string;
  status?: string;
  findBy?: string;
  email?: string;
  categoryId?: string;
  marked?: string;
}

interface ISort {
  field: CustomerSortField;
  order: SortOrder;
}

const SortableHeaderCell: React.FC<{
  label: string;
  field: CustomerSortField;
  sort: ISort | null;
  onSort: (field: CustomerSortField) => void;
}> = ({ label, field, sort, onSort }) => {
  const isActive = sort?.field === field;
  return (
    <TableCell header>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSort(field);
        }}
        className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-zinc-100"
      >
        {label}
        {isActive ? (
          sort!.order === SortOrder.ASC ? (
            <ArrowUp size={13} />
          ) : (
            <ArrowDown size={13} />
          )
        ) : (
          <ArrowUpDown size={13} className="opacity-40" />
        )}
      </button>
    </TableCell>
  );
};

export const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: categoriesData } = useCategories();
  const categoryId = searchParams.get("categoryId");

  const page = Math.max(1, +(searchParams.get("page") ?? 1));
  const sortField = searchParams.get("sortField") as CustomerSortField | null;
  const sortOrder = searchParams.get("sortOrder") as SortOrder | null;
  const sort: ISort | null = sortField && sortOrder ? { field: sortField, order: sortOrder } : null;

  const { toggleMarked } = useToggleCustomerMarked();

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      search: searchParams.get("search") ?? "",
      status: searchParams.get("status") ?? "",
      findBy: searchParams.get("findBy") ?? "",
      email: searchParams.get("email") ?? "",
      categoryId: searchParams.get("categoryId") ?? "",
      marked: searchParams.get("marked") ?? "",
    },
  });

  const navigateWithParams = (overrides: Record<string, any>) => {
    const values = {
      search: searchParams.get("search") ?? "",
      status: searchParams.get("status") ?? "",
      findBy: searchParams.get("findBy") ?? "",
      email: searchParams.get("email") ?? "",
      categoryId: searchParams.get("categoryId") ?? "",
      marked: searchParams.get("marked") ?? "",
      sortField: searchParams.get("sortField") ?? "",
      sortOrder: searchParams.get("sortOrder") ?? "",
      page: String(page),
      ...overrides,
    };
    navigate({
      pathname: "/customers",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const onSubmit = () => {
    navigateWithParams({ ...form.getValues(), page: "1" });
  };

  const handleSort = (field: CustomerSortField) => {
    if (!sort || sort.field !== field) {
      navigateWithParams({ sortField: field, sortOrder: SortOrder.ASC, page: "1" });
    } else if (sort.order === SortOrder.ASC) {
      navigateWithParams({ sortField: field, sortOrder: SortOrder.DESC, page: "1" });
    } else {
      navigateWithParams({ sortField: "", sortOrder: "", page: "1" });
    }
  };

  const handlePageChange = (nextPage: number) => {
    navigateWithParams({ page: String(nextPage) });
  };

  const where: any = {};
  const search = searchParams.get("search");
  const status = searchParams.get("status");
  const findBy = searchParams.get("findBy");
  const email = searchParams.get("email");
  const marked = searchParams.get("marked");
  if (search) where.search = search;
  if (categoryId) where.categoryId = +categoryId;
  if (status) where.status = status;
  if (findBy) where.findBy = findBy;
  if (email) where.email = email;
  if (marked) where.marked = marked === "true";
  if (sort) where.sortBy = [{ field: sort.field, order: sort.order }];

  const { data, loading } = useCustomers({ where });

  const handleToggleMarked = (e: React.MouseEvent, customerId: number, currentlyMarked: boolean) => {
    e.stopPropagation();
    toggleMarked(customerId, !currentlyMarked);
  };

  const allResults = data?.customers?.results ?? [];
  const total = data?.customers?.total ?? allResults.length;
  const pageCount = Math.max(1, Math.ceil(allResults.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedResults = allResults.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <>
      <Header
        title="Clients"
        subtitle={
          categoryId ? "Clients filtrés par catégorie" : "Liste des clients"
        }
        buttons={[
          {
            actionText: "Importer un lot",
            bgColor: "gray",
            textColor: "white",
            link: `/customers/import`,
            icon: <SendIcon />,
          },
          {
            actionText: "Nouveau client",
            bgColor: "indigo",
            textColor: "white",
            link: `/customer/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        {categoryId && (
          <div className="card mb-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Filtré par catégorie
            </span>
            <span
              className="text-indigo-600 text-sm cursor-pointer hover:underline"
              onClick={() => navigate("/customers")}
            >
              Retirer le filtre
            </span>
          </div>
        )}

        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder="Rechercher un client..."
          advancedFilters={
            <>
              <Select label="Catégorie" className="w-full" {...form.register("categoryId")}>
                <option value="">Toutes</option>
                {categoriesData?.categories?.results?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              <Select label="Statut" className="w-full" {...form.register("status")}>
                <option value="">Tous</option>
                {Object.keys(CustomerStatus).map((key) => (
                  <option key={key} value={key}>
                    {customerStatusLabel(key)}
                  </option>
                ))}
              </Select>
              <Select label="Trouvé via" className="w-full" {...form.register("findBy")}>
                <option value="">Tous</option>
                {Object.keys(FindBySource).map((key) => (
                  <option key={key} value={key}>
                    {findBySourceLabel(key)}
                  </option>
                ))}
              </Select>
              <Input
                label="Email"
                className="w-full"
                {...form.register("email")}
                placeholder="contact@client.fr"
              />
              <Select label="Marqué" className="w-full" {...form.register("marked")}>
                <option value="">Tous</option>
                <option value="true">Clients marqués</option>
                <option value="false">Non marqués</option>
              </Select>
            </>
          }
        />

        {!loading && !!allResults.length && (
          <p className="text-sm text-gray-500 dark:text-zinc-400 mb-2">
            {total} {total === 1 ? "résultat" : "résultats"}
          </p>
        )}

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !allResults.length ? (
            <EmptyList text="Aucun client" />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow hover={false}>
                    <TableCell header align="center" className="w-8">
                      <Star size={14} className="mx-auto opacity-40" />
                    </TableCell>
                    <SortableHeaderCell
                      label="Nom"
                      field={CustomerSortField.name}
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableHeaderCell
                      label="Site"
                      field={CustomerSortField.site}
                      sort={sort}
                      onSort={handleSort}
                    />
                    <TableCell header align="center">
                      Statut
                    </TableCell>
                    <TableCell header align="center">
                      Analyse
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedResults.map((customer) => (
                    <TableRow
                      key={`customer-${customer.id}`}
                      onClick={() => navigate(`/customer/${customer.id}`)}
                    >
                      <TableCell align="center">
                        <button
                          type="button"
                          title={
                            customer.marked
                              ? `Marqué par ${customer.markedBy?.firstname ?? ""} ${
                                  customer.markedBy?.lastname ?? ""
                                }`.trim()
                              : "Marquer ce client"
                          }
                          onClick={(e) => handleToggleMarked(e, customer.id, !!customer.marked)}
                          className={
                            customer.marked
                              ? "text-amber-500 hover:text-amber-600"
                              : "text-gray-300 hover:text-amber-500"
                          }
                        >
                          <Star size={16} fill={customer.marked ? "currentColor" : "none"} />
                        </button>
                      </TableCell>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell className="max-w-[160px] truncate">
                        {customer.site ?? "-"}
                      </TableCell>
                      <TableCell align="center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            customer.status === CustomerStatus.Converti
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {customerStatusLabel(customer.status)}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <div className="flex items-center justify-center gap-1.5">
                          {customer.analyseDone && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              Analysé
                            </span>
                          )}
                          {customer.problemsDetected && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Problèmes
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination page={currentPage} pageCount={pageCount} onPageChange={handlePageChange} />
            </>
          )}
        </div>
      </div>
    </>
  );
};
